/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode, useCallback, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import useControlled from '@atlaskit/ds-lib/use-controlled';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';
import { UniversalCreateBFFProvider } from '@atlassian/navigation-bff-data-provider/universal-create';
import { NavigationUFOSegment } from '@atlassian/navigation-perf-utils';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { MenuStateContext } from '../../../context';
import type { MenuStateContextProps, UniversalCreateButtonCombinedProps } from '../../../types';
import { useIsResponsiveView } from '../../../utils';
import AnalyticsWrapper from '../AnalyticsWrapper';
import { EntryPointPopup } from '../EntryPointPopup';
import { LoadingMenu } from '../LoadingMenu';
import { TriggerButton } from '../TriggerButton';

import { popupContentEntryPoint } from './entrypoint';
import { useJsChunkLoadTrack } from './hooks/use-js-chunk-load-track';

function UniversalCreateButtonLazyInner(props: UniversalCreateButtonCombinedProps) {
	const {
		children,
		testId,
		onClick,
		onClose,
		isOpen,
		enableTriggerPulse = false,
		triggerButtonSpotlightTarget,
		isNav4Enabled = true,
		buttonTheme,
		onMouseEnter,
		onMouseLeave,
		shouldRenderToParent,
		buttonOnly = false,
	} = props;
	const isButtonOnly = buttonOnly;
	const primaryActionItem = 'primaryActionItem' in props ? props.primaryActionItem : undefined;
	const isResponsive = useIsResponsiveView();

	// Track JS chunk load time - must be called before the lazy chunk loads
	useJsChunkLoadTrack();

	const isOpenStateRefactor = fg('navx-4182-uc-open-state-refactor');

	const [isLocalOpen, setLocalIsOpen] = useControlled(isOpen, () => false);

	const handleTriggerButtonClick = useCallback(() => {
		if (isOpenStateRefactor) {
			setLocalIsOpen(!isLocalOpen);
		}
		// Notify parent
		onClick?.();
	}, [isOpenStateRefactor, isLocalOpen, setLocalIsOpen, onClick]);

	const handleClose = useCallback(() => {
		if (isOpenStateRefactor) {
			setLocalIsOpen(false);
		}

		onClose?.();
	}, [isOpenStateRefactor, setLocalIsOpen, onClose]);

	const handleMouseEnter = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseEnter?.(event);
		},
		[onMouseEnter],
	);

	const [growthRecommendationsSessionId, setGrowthRecommendationsSessionId] = useState<
		string | undefined
	>(undefined);

	const menuStateContextValue = useMemo((): MenuStateContextProps => {
		const sections = 'sections' in props && props.sections ? props.sections : [];
		const crossAppCreationConfig =
			'crossAppCreationConfig' in props
				? (props.crossAppCreationConfig ?? ({ enabled: false } as const))
				: ({ enabled: false } as const);
		const selectedItem = 'selectedItem' in props ? props.selectedItem : undefined;
		const controlledOpen = 'isOpen' in props ? props.isOpen : undefined;

		return {
			isV2Mode: true, // to be removed
			items: [], // to be removed
			product: props.product ?? 'unknown',
			subProduct: props.subProduct,
			isNav4Enabled,
			sections,
			crossAppCreationConfig,
			suggestionConfig: { enabled: false }, // to be removed
			selectedItem,
			preventClose: props.preventClose,
			primaryActionItem,
			growthRecommendationsSessionId,
			setGrowthRecommendationsSessionId,
			...(isOpenStateRefactor ? {} : { controlledOpen }),
		};
	}, [
		props,
		isNav4Enabled,
		primaryActionItem,
		isOpenStateRefactor,
		growthRecommendationsSessionId,
	]);

	const sharedTriggerButtonProps = useMemo(
		() => ({
			testId,
			triggerButtonSpotlightTarget,
			enableTriggerPulse,
			onMouseEnter: handleMouseEnter,
			onMouseLeave,
			onClick: handleTriggerButtonClick,
			isNav4Enabled,
			buttonTheme,
		}),
		[
			testId,
			triggerButtonSpotlightTarget,
			enableTriggerPulse,
			handleMouseEnter,
			onMouseLeave,
			handleTriggerButtonClick,
			isNav4Enabled,
			buttonTheme,
		],
	);

	return (
		<MenuStateContext.Provider value={menuStateContextValue}>
			{isButtonOnly ? (
				<TriggerButton {...sharedTriggerButtonProps} buttonOnly>
					{children}
				</TriggerButton>
			) : (
				<EntryPointPopup
					{...(isOpenStateRefactor ? { isOpen: isLocalOpen } : {})}
					placement={
						(isResponsive && isNav4Enabled) || primaryActionItem ? 'bottom-end' : 'bottom-start'
					}
					// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
					shouldRenderToParent={shouldRenderToParent}
					onClose={handleClose}
					trigger={(triggerProps) => (
						<TriggerButton
							{...sharedTriggerButtonProps}
							buttonOnly={undefined} // explicitly disable buttonOnly
							isEntryPointEnabled
							triggerProps={triggerProps}
							isOpen={triggerProps['aria-expanded']}
							primaryActionItem={primaryActionItem}
						>
							{children}
						</TriggerButton>
					)}
					entryPoint={popupContentEntryPoint}
					entryPointProps={
						props as Exclude<UniversalCreateButtonCombinedProps, { buttonOnly: true }>
					}
					fallback={<LoadingMenu padded />}
				/>
			)}
		</MenuStateContext.Provider>
	);
}

function UFOWrapper({ children }: { children: ReactNode }) {
	return <NavigationUFOSegment id="universalCreateComponentLoad">{children}</NavigationUFOSegment>;
}

export function UniversalCreateButtonLazy(props: UniversalCreateButtonCombinedProps): JSX.Element {
	return (
		<UFOWrapper>
			<ConditionalRelayEnvironmentProvider>
				<BFFProvider consumer={BFFConsumerEnum.UNIVERSAL_CREATE}>
					<UniversalCreateBFFProvider>
						<AnalyticsWrapper>
							<FlagsProvider>
								<UniversalCreateButtonLazyInner {...props} />
							</FlagsProvider>
						</AnalyticsWrapper>
					</UniversalCreateBFFProvider>
				</BFFProvider>
			</ConditionalRelayEnvironmentProvider>
		</UFOWrapper>
	);
}
